import type { KeycloakConfig } from './types';

/**
 * Configuration preset types
 */
export type ConfigPreset = 'default' | 'mobile' | 'high-security' | 'long-running' | 'development';

/**
 * Configuration presets for common use cases
 *
 * These presets provide optimized configurations for different application types
 * and security requirements. All presets can be further customized by overriding
 * specific properties.
 */
export const CONFIG_PRESETS: Record<ConfigPreset, Partial<KeycloakConfig>> = {
  /**
   * Default preset - Balanced configuration for most applications
   *
   * Features:
   * - 30-day refresh token lifetime
   * - Proactive token refresh on startup + reactive on 401
   * - Silent background token refresh
   * - Session monitoring enabled
   * - Auto token refresh enabled
   *
   * Best for: Most web applications, dashboards, internal tools
   */
  default: {
    scope: 'openid profile email offline_access',
    features: {
      autoRefresh: true,
      sessionMonitor: true,
      deviceFlow: false,
    },
    silentRenew: {
      enabled: true,
      thresholdSeconds: 300, // 5 minutes
      refreshStrategy: 'both',
      maxRefreshTokenLifetime: 2592000, // 30 days
      showLoadingIndicator: false,
      loadingMessage: 'Refreshing session...',
    },
    advanced: {
      responseType: 'code',
      codeChallengeMethod: 'S256',
      loadUserInfo: true,
      automaticSilentSignIn: false,
    },
  },

  /**
   * Mobile/PWA preset - Optimized for mobile and progressive web apps
   *
   * Features:
   * - 30-day refresh token lifetime
   * - Proactive startup refresh (apps close frequently)
   * - Loading indicator during refresh (better UX for app reopening)
   * - Session monitoring enabled
   *
   * Best for: Mobile apps, PWAs, apps that frequently close/reopen
   */
  mobile: {
    scope: 'openid profile email offline_access',
    features: {
      autoRefresh: true,
      sessionMonitor: true,
      deviceFlow: false,
    },
    silentRenew: {
      enabled: true,
      thresholdSeconds: 300,
      refreshStrategy: 'startup', // Focus on startup refresh
      maxRefreshTokenLifetime: 2592000, // 30 days
      showLoadingIndicator: true, // Show loading for better UX
      loadingMessage: 'Restoring your session...',
    },
    advanced: {
      responseType: 'code',
      codeChallengeMethod: 'S256',
      loadUserInfo: true,
      automaticSilentSignIn: false,
    },
  },

  /**
   * High Security preset - Strict security for sensitive applications
   *
   * Features:
   * - 24-hour forced re-authentication
   * - Proactive + reactive token refresh
   * - 10-minute refresh threshold (earlier refresh)
   * - Silent refresh (no UI hints about security measures)
   * - Session monitoring enabled
   *
   * Best for: Banking, healthcare, admin panels, sensitive data applications
   */
  'high-security': {
    scope: 'openid profile email offline_access',
    features: {
      autoRefresh: true,
      sessionMonitor: true,
      deviceFlow: false,
    },
    silentRenew: {
      enabled: true,
      thresholdSeconds: 600, // 10 minutes (more aggressive)
      refreshStrategy: 'both',
      maxRefreshTokenLifetime: 86400, // 24 hours only
      showLoadingIndicator: false, // Silent for security
      loadingMessage: 'Validating session...',
    },
    advanced: {
      responseType: 'code',
      codeChallengeMethod: 'S256',
      loadUserInfo: true,
      automaticSilentSignIn: false,
    },
  },

  /**
   * Long-Running preset - Optimized for desktop apps and long sessions
   *
   * Features:
   * - 7-day refresh token lifetime
   * - Proactive + reactive token refresh
   * - Silent background refresh
   * - Session monitoring enabled
   *
   * Best for: Desktop applications, monitoring dashboards, always-on tools
   */
  'long-running': {
    scope: 'openid profile email offline_access',
    features: {
      autoRefresh: true,
      sessionMonitor: true,
      deviceFlow: false,
    },
    silentRenew: {
      enabled: true,
      thresholdSeconds: 300,
      refreshStrategy: 'both',
      maxRefreshTokenLifetime: 604800, // 7 days
      showLoadingIndicator: false,
      loadingMessage: 'Refreshing session...',
    },
    advanced: {
      responseType: 'code',
      codeChallengeMethod: 'S256',
      loadUserInfo: true,
      automaticSilentSignIn: false,
    },
  },

  /**
   * Development preset - Convenient settings for development
   *
   * Features:
   * - No refresh token lifetime limit (never expires)
   * - Proactive + reactive token refresh
   * - Loading indicators visible (easier debugging)
   * - Session monitoring enabled
   * - More verbose feedback
   *
   * Best for: Local development, testing, debugging
   * WARNING: Do not use in production!
   */
  development: {
    scope: 'openid profile email offline_access',
    features: {
      autoRefresh: true,
      sessionMonitor: true,
      deviceFlow: false,
    },
    silentRenew: {
      enabled: true,
      thresholdSeconds: 300,
      refreshStrategy: 'both',
      maxRefreshTokenLifetime: 0, // No limit (for convenience)
      showLoadingIndicator: true, // Show for debugging
      loadingMessage: '[DEV] Refreshing tokens...',
    },
    advanced: {
      responseType: 'code',
      codeChallengeMethod: 'S256',
      loadUserInfo: true,
      automaticSilentSignIn: false,
    },
  },
};

/**
 * Get a configuration preset by name
 *
 * @param preset - The preset name
 * @returns Partial Keycloak configuration for the preset
 *
 * @example
 * ```typescript
 * const config = getConfigPreset('mobile');
 * ```
 */
export function getConfigPreset(preset: ConfigPreset): Partial<KeycloakConfig> {
  return CONFIG_PRESETS[preset];
}

/**
 * Merge a preset with custom configuration
 *
 * Custom configuration takes precedence over preset values.
 * Use this to start with a preset and override specific properties.
 *
 * @param preset - The preset name
 * @param customConfig - Custom configuration to merge
 * @returns Merged configuration
 *
 * @example
 * ```typescript
 * const config = mergeWithPreset('mobile', {
 *   silentRenew: {
 *     maxRefreshTokenLifetime: 604800, // Override to 7 days
 *   }
 * });
 * ```
 */
export function mergeWithPreset(
  preset: ConfigPreset,
  customConfig: Partial<KeycloakConfig>
): Partial<KeycloakConfig> {
  const presetConfig = getConfigPreset(preset);

  return {
    ...presetConfig,
    ...customConfig,
    features: {
      ...presetConfig.features,
      ...customConfig.features,
    },
    silentRenew: {
      ...presetConfig.silentRenew,
      ...customConfig.silentRenew,
    },
    advanced: {
      ...presetConfig.advanced,
      ...customConfig.advanced,
    },
    events: {
      ...presetConfig.events,
      ...customConfig.events,
    },
  };
}
